// Datadog-based implementation of `logClient`

import { datadogLogs, LogsEvent } from "@datadog/browser-logs";
import { LogClient, LogFunction } from "./logClient";

const local = "local";
//We need to do this so we can use the webpack define plugin
declare var APPVERSION: string;
const environment = window.appConfig ? window.appConfig.HR_ENVIRONMENT : local;

// URLs that are not actionable when they fail (typically ad-blocked). See CF-5903.
export const NOISY_URL_PATTERNS: RegExp[] = [
  /analytics-events[\w.-]*\.happyreturns\.(com|tech)/i,
  /google-analytics\.com/i,
  /googletagmanager\.com/i,
];

export const isNoisyAnalyticsUrl = (url: string | undefined): boolean =>
  !!url && NOISY_URL_PATTERNS.some((pattern) => pattern.test(url));

// `status_code === 0` indicates the request never got a response (ad blocker, aborted, offline).
export const isClientBlockedFetch = (event: LogsEvent): boolean => {
  if (event.origin !== "network") return false;
  const statusCode = event.http?.status_code;
  return statusCode === 0 || statusCode === undefined;
};

export const buildBeforeSend = () => (event: LogsEvent): boolean => {
  const url = event.http?.url;

  if (isNoisyAnalyticsUrl(url)) {
    return false;
  }

  if (event.status === "error" && isClientBlockedFetch(event)) {
    event.status = "warn";
  }

  return true;
};

datadogLogs.init({
  clientToken: "pub42b876fb3430d796067ca4bd42f3cad2", // safe to hard-code
  env: environment,
  forwardConsoleLogs: environment != local ? "all" : [],
  forwardErrorsToLogs: environment != local,
  service: "returns-app",
  version: APPVERSION,
  sessionSampleRate: 100,
  site: "datadoghq.com",
  telemetrySampleRate: 0,
  beforeSend: buildBeforeSend(),
});

class DatadogLogger implements LogClient {
  // Bind to set values regardless of how these functions are used
  constructor() {
    this.Debug = this.Debug.bind(this);
    this.Error = this.Error.bind(this);
    this.Info = this.Info.bind(this);
    this.Warning = this.Warning.bind(this);
    this.log = this.log.bind(this);
    this.SetGlobalContextProperty = this.SetGlobalContextProperty.bind(this);
  }

  /**
   * NOTE: the following functions pass `(m, mc) => datadogLogs.logger.*(m, mc)` as the first parameter to #log
   * this is intentional because we want to retain the value of `this` from whatever context the function was
   * called from. If we instead just passed `datadogLogs.logger.*` directly it would result in an error.
   *
   * for more information: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/this#callbacks
   */

  Debug(message: string, messageContext?: object): void {
    this.log((message, messageContext) => datadogLogs.logger.debug(message, messageContext), message, messageContext);
  }

  Error(message: string, messageContext?: object): void {
    this.log((message, messageContext) => datadogLogs.logger.error(message, messageContext), message, messageContext);
  }

  Info(message: string, messageContext?: object): void {
    this.log((message, messageContext) => datadogLogs.logger.info(message, messageContext), message, messageContext);
  }

  SetGlobalContextProperty(key: string, value: any) {
    datadogLogs.setGlobalContextProperty(key, value);
  }

  Warning(message: string, messageContext?: object): void {
    this.log((message, messageContext) => datadogLogs.logger.warn(message, messageContext), message, messageContext);
  }

  log = (logFunc: LogFunction, message: string, messageContext?: object) => {
    if (environment != local) {
      logFunc(message, messageContext);
    }
  };
}
// Do NOT export NullLogger
class NullLogger implements LogClient {
  Debug(message: string, messageContext?: object): void {
    // Do nothing, because this is the NullLogger
  }

  Error(message: string, messageContext?: object): void {
    // Do nothing, because this is the NullLogger
  }

  Info(message: string, messageContext?: object): void {
    // Do nothing, because this is the NullLogger
  }

  SetGlobalContextProperty(key: string, value: any) {
    // Do nothing, because this is the NullLogger
  }

  Warning(message: string, messageContext?: object): void {
    // Do nothing, because this is the NullLogger
  }
}

const createDatadogLogClient = (): LogClient => {
  if (process.env.JEST_WORKER_ID) {
    return new NullLogger();
  }

  return new DatadogLogger();
};

export const DatadogLogClient: LogClient = createDatadogLogClient();
